import { useState, useEffect, useCallback } from 'react';
import { usersApi, User, UpdateUserDto, UserDetails } from '../../../api/users';
import { toast } from 'sonner';
import { UseUsersReturn } from '../types';

const ITEMS_PER_PAGE = 10;

export function useUsers(): UseUsersReturn {
  const [users, setUsers] = useState<User[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [subscriptionFilter, setSubscriptionFilter] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState<UpdateUserDto>({});
  const [viewingUser, setViewingUser] = useState<UserDetails | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [premiumUser, setPremiumUser] = useState<User | null>(null);
  const [isPremiumDialogOpen, setIsPremiumDialogOpen] = useState(false);
  const [isPremiumSaving, setIsPremiumSaving] = useState(false);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: any = {
        page: currentPage,
        limit: ITEMS_PER_PAGE,
      };

      if (searchQuery) {
        params.search = searchQuery;
      }

      if (roleFilter !== 'all') {
        params.role = roleFilter;
      }

      if (subscriptionFilter !== 'all') {
        params.isSubscribed = subscriptionFilter;
      }

      const response = await usersApi.getUsers(params);
      setUsers(response.users);
      setTotalPages(response.totalPages);
      setTotal(response.total);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch users');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchQuery, roleFilter, subscriptionFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleRoleFilter = (value: string) => {
    setRoleFilter(value);
    setCurrentPage(1);
  };

  const handleSubscriptionFilter = (value: string) => {
    setSubscriptionFilter(value);
    setCurrentPage(1);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setEditFormData({
      email: user.email || '',
      username: user.username,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      role: user.role,
      isSubscribed: user.isSubscribed,
    });
    setIsEditDialogOpen(true);
  };

  const handleView = async (user: User) => {
    setIsViewDialogOpen(true);
    setIsLoadingDetails(true);
    try {
      const details = await usersApi.getUserDetails(user.id);
      setViewingUser(details);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load user details');
      setIsViewDialogOpen(false);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleSave = async () => {
    if (!editingUser) return;

    try {
      await usersApi.updateUser(editingUser.id, editFormData);
      toast.success('User updated successfully');
      setIsEditDialogOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update user');
    }
  };

  const handleOpenPremium = (user: User) => {
    setPremiumUser(user);
    setIsPremiumDialogOpen(true);
  };

  const handleGrantPremium = async (subscribedEnd: string) => {
    if (!premiumUser) return;
    setIsPremiumSaving(true);
    try {
      await usersApi.grantPremium(premiumUser.id, subscribedEnd);
      toast.success(`Premium granted until ${new Date(subscribedEnd).toLocaleDateString()}`);
      setIsPremiumDialogOpen(false);
      setPremiumUser(null);
      fetchUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to grant premium');
    } finally {
      setIsPremiumSaving(false);
    }
  };

  const handleRevokePremium = async () => {
    if (!premiumUser) return;
    setIsPremiumSaving(true);
    try {
      await usersApi.revokePremium(premiumUser.id);
      toast.success('Premium revoked');
      setIsPremiumDialogOpen(false);
      setPremiumUser(null);
      fetchUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to revoke premium');
    } finally {
      setIsPremiumSaving(false);
    }
  };

  return {
    users,
    total,
    totalPages,
    currentPage,
    isLoading,
    searchQuery,
    roleFilter,
    subscriptionFilter,
    editingUser,
    isEditDialogOpen,
    editFormData,
    viewingUser,
    isViewDialogOpen,
    isLoadingDetails,
    premiumUser,
    isPremiumDialogOpen,
    isPremiumSaving,
    setCurrentPage,
    handleSearch,
    handleRoleFilter,
    handleSubscriptionFilter,
    handleEdit,
    handleView,
    handleSave,
    handleOpenPremium,
    handleGrantPremium,
    handleRevokePremium,
    setIsEditDialogOpen,
    setIsViewDialogOpen,
    setIsPremiumDialogOpen,
    setEditFormData,
  };
}

export { ITEMS_PER_PAGE };
